import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  setLogLevel,
  type Firestore,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  type Functions,
} from 'firebase/functions';
import { shouldUseFirestoreFetchStreams } from './firestore-transport';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
};

const requiredConfig = [
  firebaseConfig.apiKey,
  firebaseConfig.authDomain,
  firebaseConfig.projectId,
  firebaseConfig.appId,
];

const functionsRegion = 'europe-central2';
export const useFirebaseEmulators =
  import.meta.env.VITE_FIREBASE_USE_EMULATORS === 'true';
const firestoreLogLevelStorageKey = 'forma:firestoreLogLevel';
type FirestoreLogLevel = Parameters<typeof setLogLevel>[0];
const firestoreLogLevels = new Set<FirestoreLogLevel>([
  'debug',
  'error',
  'silent',
]);
const authEmulatorHost =
  import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST ?? '127.0.0.1';
const authEmulatorPort = Number(
  import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT ?? '9199',
);
const firestoreEmulatorHost =
  import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_HOST ?? '127.0.0.1';
const firestoreEmulatorPort = Number(
  import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT ?? '8180',
);
const functionsEmulatorHost =
  import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST ?? '127.0.0.1';
const functionsEmulatorPort = Number(
  import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT ?? '5101',
);

export const isFirebaseConfigured = requiredConfig.every(
  (value) => typeof value === 'string' && value.length > 0,
);

function isFirestoreLogLevel(value: string): value is FirestoreLogLevel {
  return firestoreLogLevels.has(value as FirestoreLogLevel);
}

function readFirestoreLogLevel(): FirestoreLogLevel | null {
  if (!useFirebaseEmulators && !import.meta.env.DEV) {
    return null;
  }

  const envLogLevel = import.meta.env.VITE_FIREBASE_FIRESTORE_LOG_LEVEL;
  const storedLogLevel =
    typeof localStorage === 'undefined'
      ? null
      : localStorage.getItem(firestoreLogLevelStorageKey);
  const logLevel = envLogLevel || storedLogLevel || '';

  return isFirestoreLogLevel(logLevel) ? logLevel : null;
}

const firestoreLogLevel = readFirestoreLogLevel();

if (firestoreLogLevel) {
  setLogLevel(firestoreLogLevel);
}

let auth: Auth | null = null;
let db: Firestore | null = null;
let firebaseApp: FirebaseApp | null = null;
let functions: Functions | null = null;

if (isFirebaseConfigured) {
  firebaseApp = initializeApp(firebaseConfig);
  // Select persistence before Auth installs its cross-tab storage listener.
  auth = initializeAuth(firebaseApp, {
    persistence: browserLocalPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  });
  const useFirestoreFetchStreams = shouldUseFirestoreFetchStreams();
  try {
    db = initializeFirestore(firebaseApp, {
      localCache:
        useFirebaseEmulators || typeof indexedDB === 'undefined'
          ? memoryLocalCache()
          : persistentLocalCache({
              tabManager: persistentMultipleTabManager(),
            }),
      experimentalForceLongPolling: !useFirestoreFetchStreams,
      useFetchStreams: useFirestoreFetchStreams,
    } as Parameters<typeof initializeFirestore>[1]);
  } catch (error) {
    console.warn(
      'Persistent Firestore cache is unavailable; using memory cache.',
      error,
    );
    db = getFirestore(firebaseApp);
  }
  functions = getFunctions(firebaseApp, functionsRegion);

  if (useFirebaseEmulators) {
    connectAuthEmulator(
      auth,
      `http://${authEmulatorHost}:${authEmulatorPort}`,
      {
        disableWarnings: true,
      },
    );
    connectFirestoreEmulator(db, firestoreEmulatorHost, firestoreEmulatorPort);
    connectFunctionsEmulator(
      functions,
      functionsEmulatorHost,
      functionsEmulatorPort,
    );
  }
}

export { auth, db, firebaseApp, functions };
